import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Upload, Database, Loader2, AlertTriangle, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "hebeh_auth_token";

// URL del entorno preview (donde vive la data vieja antes del deploy)
const PREVIEW_URL = "https://hebeh-business.preview.emergentagent.com";

// Detecta si estamos en preview o producción
const IS_PRODUCTION = typeof window !== "undefined" && window.location.hostname === "sistema.hebeh.com.mx";

export default function DbMigrationCard() {
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [showHelp, setShowHelp] = useState(IS_PRODUCTION);

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem(STORAGE_KEY) || "";
      const res = await axios.get(`${API}/admin/db/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `hebeh-db-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export descargado correctamente");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Error al exportar");
    } finally {
      setExporting(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(
      overwrite
        ? `¿DESTRUCTIVO? Se eliminarán todas las colecciones actuales y se reemplazarán con ${file.name}. ¿Continuar?`
        : `Se importará ${file.name} solo en colecciones vacías (modo seguro). ¿Continuar?`
    )) {
      e.target.value = "";
      return;
    }
    try {
      setRestoring(true);
      setLastResult(null);
      const token = localStorage.getItem(STORAGE_KEY) || "";
      const fd = new FormData();
      fd.append("file", file);
      fd.append("overwrite", overwrite ? "true" : "false");
      const res = await axios.post(`${API}/admin/db/restore`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000,
      });
      setLastResult(res.data?.summary || {});
      toast.success("Restauración completada. Recarga la página para ver los datos.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Error al restaurar");
    } finally {
      setRestoring(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div
      className="bg-slate-900/60 border border-orange-500/30 rounded-2xl p-5 mb-6"
      data-testid="db-migration-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-orange-500/20 rounded-lg">
          <Database className="w-5 h-5 text-orange-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold text-base">
            Migración de Datos (Admin)
            {IS_PRODUCTION && (
              <span className="ml-2 text-xs bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded">
                Estás en PRODUCCIÓN
              </span>
            )}
          </h3>
          <p className="text-slate-400 text-xs mt-1">
            Usa esta herramienta para traer tus cotizaciones, vehículos, clientes, OCs y más desde el preview a producción.
          </p>
        </div>
        <button
          onClick={() => setShowHelp((v) => !v)}
          className="text-xs text-orange-300 hover:text-orange-200 flex items-center gap-1"
          data-testid="db-migration-help-toggle"
        >
          {showHelp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          ¿Cómo migrar?
        </button>
      </div>

      {showHelp && (
        <div className="mb-4 p-4 bg-slate-950/60 border border-slate-800 rounded-lg text-xs text-slate-300 space-y-3">
          <p className="font-semibold text-orange-300 text-sm">Pasos para migrar datos del preview a producción:</p>
          <ol className="list-decimal list-inside space-y-2 pl-1">
            <li>
              Abre el <strong>preview</strong> en una nueva pestaña (ahí viven los datos antes del deploy):<br />
              <a
                href={PREVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-orange-400 hover:text-orange-300 underline mt-1"
                data-testid="db-migration-preview-link"
              >
                {PREVIEW_URL}
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>Ingresa con tus mismas credenciales (correo + contraseña + 2FA).</li>
            <li>
              Dentro del preview, ve a la pestaña <strong>Empresa</strong> y haz clic en{" "}
              <strong>"Descargar export (JSON)"</strong>. Se descargará un archivo grande (puede pesar ~90 MB)
              a tu carpeta de <strong>Descargas</strong> de tu PC.
            </li>
            <li>
              Regresa <strong>aquí en producción</strong> (sistema.hebeh.com.mx), haz clic en{" "}
              <strong>"Subir export para restaurar"</strong> abajo y selecciona el archivo que acabas de descargar.
            </li>
            <li>
              Espera a que termine (tarda 1–3 minutos). Luego <strong>recarga la página (F5)</strong> y verás
              tus 18 cotizaciones, 16 vehículos, 5 clientes, 4 OCs, etc.
            </li>
          </ol>
          <p className="text-amber-300 mt-2">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            El archivo puede contener imágenes en base64 por eso es grande. Es normal.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <button
          onClick={handleExport}
          disabled={exporting}
          data-testid="db-migration-export-btn"
          className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 hover:border-orange-500/50 text-white text-sm font-medium transition disabled:opacity-50"
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {exporting ? "Exportando..." : "Descargar export (JSON)"}
        </button>

        <label
          className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 hover:border-orange-500/50 text-white text-sm font-medium transition cursor-pointer ${
            restoring ? "opacity-50 cursor-wait" : ""
          }`}
          data-testid="db-migration-restore-label"
        >
          {restoring ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {restoring ? "Restaurando..." : "Subir export para restaurar"}
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={handleRestore}
            disabled={restoring}
            data-testid="db-migration-restore-input"
          />
        </label>
      </div>

      <label className="flex items-center gap-2 mt-3 text-xs text-slate-400 cursor-pointer">
        <input
          type="checkbox"
          checked={overwrite}
          onChange={(e) => setOverwrite(e.target.checked)}
          data-testid="db-migration-overwrite-checkbox"
          className="rounded"
        />
        <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
        <span>
          Sobreescribir datos existentes (DESTRUCTIVO — elimina las colecciones antes de importar)
        </span>
      </label>

      {lastResult && (
        <div className="mt-4 p-3 bg-slate-950/60 border border-slate-800 rounded-lg text-xs font-mono text-slate-300 max-h-48 overflow-auto" data-testid="db-migration-result">
          {Object.entries(lastResult).map(([k, v]) => (
            <div key={k}>
              <span className="text-slate-500">{k}:</span> {String(v)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
